import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { commonComponentImages } from '@images/common-components-images';
import { commonImages } from '@images/common-images';
import { ApiService } from '@services/api/api.service';
import { ListingApprovalService } from '@services/api/listing-approval.service';
import { ListingPropertiesService } from '@services/api/listing-properties.service';
import { SharedService } from '@services/api/shared.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { toWallClock } from '@services/helpers/wall-clock.util';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { SharedDataService } from '@services/shared-data.service';
import { ReduxService } from '@state-management/redux.service';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-preview-listing',
  templateUrl: './preview-listing.component.html',
  styleUrls: ['./preview-listing.component.scss'],
})
export class PreviewListingComponent {
  @ViewChild('ops') ops!: OverlayPanel;
  popupLoad: boolean = false;
  title: string = 'Preview Listing';
  @Input() id: Observable<any> = null;
  @Input() listingId: boolean;
  @Input() isEditShow: boolean;
  @Input() pageLoad: boolean;
  @Output() openEditModel = new EventEmitter<boolean>();
  public Editor = ClassicEditor;
  show_email_template: boolean = false;
  common_images = commonImages;
  images = commonComponentImages;
  S3Path = environment.S3 + '/';
  public email_subject = null;
  public email_template = null;
  public email_message = null;
  publish_date: any;
  email_to: any[] = [];
  vendCommentsValid;
  email_cc: any[] = [];
  email_bcc: any[] = [];
  emailTemplates: any[] = [];
  Teams: any;
  roles: any;
  originalAssigns = [];
  listingDetails: any;
  dayAgo: any;
  editPropertyId: any;
  readWritePermissions: { can_delete: boolean; can_write: boolean; can_approve: boolean; can_resolve: boolean } = {
    can_approve: false,
    can_delete: false,
    can_resolve: false,
    can_write: false,
  };
  public livePlan = null;
  public schedule = null;
  emailBtnDisabled: boolean = false;
  currentIndex: number = 0;
  displayedImages = [];
  imagesPath: any;
  imageIndex: any;
  listingComments: any[] = [];
  vendorComments: any[] = [];
  public userRole = Crypto.decrypt(this.localStorageService.GetItem('role')) || '';

  selectAssign: any[] = [
    { name: 'Staff', value: 'staff' },
    { name: 'Team', value: 'team' },
    { name: 'User Role', value: 'user_role' },
    { name: 'Contact', value: 'contact' },
  ];

  constructor(
    private api: ApiService,
    private redux: ReduxService,
    private listingPropertiesService: ListingPropertiesService,
    private toastr: ToastrService,
    public sharedDataService: SharedDataService,
    private localStorageService: LocalStorageService,
    public lmService: LeadManagementService,
    public router: Router,
    private _sharedService: SharedService,
    private _listingApprovalService: ListingApprovalService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.listingId?.currentValue) {
      let id = changes?.listingId?.currentValue;
      this.getListingPermission(id);
      this.getListingDetails(id);
      this.getApprovalComments(id);
      this.getSettings();
    }
  }

  // Lifecycle hook: Called once the component is initialized
  ngOnInit(): void {
    // Subscribe to 'id' observable to fetch listing details when 'id' changes
    this.id?.subscribe({
      next: listingId => {
        if (listingId) {
          this.getListingPermission(listingId); // Fetch listing permissions
          this.getListingDetails(listingId); // Fetch listing details
          this.getApprovalComments(listingId); // Fetch approval comments
          this.getSettings(); // Fetch settings
        }
      },
    });

    // Subscribe to approval notification updates
    this.sharedDataService.approvalNotificationUpdatedObservable.subscribe({
      next: () => {
        this.editPropertyId && this.getApprovalComments(); // Fetch approval comments if property is being edited
      },
    });
  }

  // Fetch settings related to vendor comments visibility
  getSettings() {
    this.listingPropertiesService.getVisibility().subscribe({
      next: response => {
        if (response.data) {
          this.vendCommentsValid = response.data;
        }
      },
    });
  }

  // Add a new email recipient to the 'To' field
  addEmailTo() {
    this.email_to?.push({ type: null, email_to: null });
  }

  // Remove an email recipient from the 'To' field
  removeEmailTo(i) {
    this.email_to?.splice(i, 1);
  }

  // Add a new email recipient to the 'CC' field
  addEmailCC() {
    this.email_cc?.push({ type: null, email_cc: null });
  }

  // Remove an email recipient from the 'CC' field
  removeEmailCC(i) {
    this.email_cc?.splice(i, 1);
  }

  // Add a new email recipient to the 'BCC' field
  addEmailBCC() {
    this.email_bcc?.push({ type: null, email_bcc: null });
  }

  // Remove an email recipient from the 'BCC' field
  removeEmailBCC(i) {
    this.email_bcc?.splice(i, 1);
  }

  // Open the address overlay panel and prepare email-related data
  openAddressOverlayPanel() {
    this.email_to = [];
    // Populate 'To' field with seller/owner contacts
    this.listingDetails?.property_contacts?.forEach(x => {
      if (x?.type?.toString()?.toLocaleLowerCase()?.includes('seller') || x?.type?.toString()?.toLocaleLowerCase()?.includes('owner')) {
        this.email_to?.push({ type: 'contact', email_to: [x?._id] });
      }
    });

    this.show_email_template = true; // Show email template
    this.getEmailTemplate(); // Fetch email templates
    this.getTeams(); // Fetch teams
    this.getAgents(); // Fetch agents
    this.getRoles(); // Fetch roles
  }

  // Fetch email templates based on office ID
  getEmailTemplate() {
    this._sharedService.getEmailTemplate().subscribe({
      next: response => {
        this.emailTemplates = response.data.filter(x => x.type?.toLocaleLowerCase() == 'listing preview');
        if (this.emailTemplates?.length > 0) {
          this.email_template = this.emailTemplates[0]?._id;
          this.email_subject = this.emailTemplates[0]?.subject;
          this.email_message = this.emailTemplates[0]?.body;
        }
      },
    });
  }

  // Update email subject and message when a template is changed
  onChange(e) {
    this.email_subject = e?.subject;
    this.email_message = e?.body;
  }

  // Fetch teams associated with the office
  getTeams() {
    if (this.api.getOfficeId()) {
      this._sharedService.getDropDownItems('teams').subscribe({
        next: response => {
          this.Teams = response.data;
        },
      });
    }
  }

  // Fetch user roles and filter them based on conditions
  getRoles() {
    this._sharedService.getDropDownItems(`roles`).subscribe({
      next: data => {
        this.roles = data.data;
        this.roles.forEach((itm, index, object) => {
          if (itm._id) {
            itm.id = itm._id; // Assign _id to id property
          }
          // Remove 'Master Admin' role if the current user is not a Master Admin
          if (this.userRole !== 'Master Admin' && itm.name == 'Master Admin' && this.userRole !== 'Master Admin') {
            object.splice(index, 1);
          }
        });
      },
    });
  }

  // Fetch agents associated with the office and sort them alphabetically
  getAgents() {
    if (this.api.getOfficeId()) {
      let url = `/users/office-staff-list?uid=${this.api.getUserId()}&officeid=${this.api.getOfficeId()}&show=staff`;
      // Call the redux service to fetch the data from the API
      this.redux.getSetProjectInfo(false, 'office_staff', url).subscribe({
        next: response => {
          const { data } = response; // Extract the data from the response
          let record = data
            .sort(function (a, b) {
              return a?.full_name.localeCompare(b?.full_name);
            })
            .map(result => ({ ...result, category: 'Staff' }));
          this.originalAssigns = JSON.parse(JSON.stringify(record)); // Clone the data to avoid mutation issues
        },
      });

      this.api
        .get('/office-staff', {
          page: null,
          per_page: 'all',
          uid: this.api.getUserId(),
          officeid: this.api.getOfficeId(),
          show: 'staff',
          include: 'groups,offices',
        })
        .subscribe({
          next: response => {
            let record = response.data
              .sort(function (a, b) {
                return a?.full_name.localeCompare(b?.full_name);
              })
              .map(result => ({ ...result, category: 'Staff' }));
            this.originalAssigns = record;
          },
          error: () => {
            this.toastr.error('Something has gone wrong'); // Show error message if API call fails
          },
        });
    }
  }

  // Trigger listing edit action by emitting an event
  listingEdit() {
    this.close(); // Close the current view/modal
    this.openEditModel.emit(true); // Emit event to open edit model
  }

  // Fetch listing details by listing ID and format data accordingly
  getListingDetails(id) {
    this.listingDetails = [];
    this.editPropertyId = id;
    this.listingPropertiesService.getListingPropertyDetailById(id).subscribe({
      next: response => {
        const { status, data, message } = response;
        if (status) {
          this.listingDetails = data;
          this.displayedImages = this.listingDetails?.property_images; // Store listing images
          this.getPublishDate(id); // Fetch listing publish date

          // Construct property address
          let address = '';
          if (this.listingDetails?.unit_no && this.listingDetails?.street_no) {
            address = this.listingDetails?.unit_no + '/' + this.listingDetails?.street_no;
          } else {
            address = this.listingDetails?.unit_no ? this.listingDetails?.unit_no : '';
            address = this.listingDetails?.street_no ? this.listingDetails?.street_no : '';
          }
          address += address.length > 0 ? ' ' : '';
          address += this.listingDetails?.street_name
            ? this.listingDetails?.street_name +
              ', ' +
              this.listingDetails?.suburb?.suburb +
              ', ' +
              this.listingDetails?.state +
              ' ' +
              this.listingDetails?.post_code
            : '';
          address = address.length > 0 ? address : this.listingDetails?.property_address;
          this.listingDetails.property_address = address;

          // Filter appointments and inspections to keep only future or ongoing ones
          let todays = moment();
          this.listingDetails.appointments_and_inspections = this.listingDetails.appointments_and_inspections?.filter(ins => {
            if (ins?.isInspection == true) {
              const eventDate = moment(toWallClock(ins?.event?.start?.dateTime));
              const eventEndDateTime = moment(toWallClock(ins?.event?.end?.dateTime));
              const isFutureDate = eventDate.isAfter(todays, 'day');
              const isSameDate = eventDate.isSame(todays, 'day') && eventEndDateTime.isAfter(todays);
              return isSameDate || isFutureDate;
            }
          });

          // Calculate the number of days since the listing was created
          let createDate = new Date(data?.created_at);
          let today = new Date();
          let day = Math.abs(createDate.valueOf() - today.valueOf());
          this.dayAgo = Math.round(day / (1000 * 3600 * 24));
        } else {
          this.toastr.error(message ? message : 'Something went wrong'); // Show error if response is unsuccessful
        }
      },
      error: error => {
        this.toastr.error(error?.error?.message ? error?.error?.message : 'Something went wrong'); // Handle API error
      },
    });
  }

  /**
   * Fetches listing permissions for a given listing ID
   * Determines if the current user has approval, resolve, delete, or edit permissions
   */
  getListingPermission(id) {
    this._listingApprovalService.getAllowedPermissionListOnListingApproval(id).subscribe({
      next: response => {
        let teamId = Crypto.decrypt(this.localStorageService.GetItem('teams_id')) || [];
        let resultPermissionApproval = response['data']?.listing_approval_detail?.some(
          element =>
            (element?.included?.toString()?.includes(this.api.getUserId()) && element?.permission?.toString()?.includes('approval')) ||
            (element?.included?.toString()?.includes(this.api.getRoleId()) && element?.permission?.toString()?.includes('approval')) ||
            (element?.included?.some(ele => teamId?.toString()?.includes(ele)) && element?.permission?.toString()?.includes('approval'))
        );
        let resultPermissionResolve = response['data']?.listing_approval_detail?.some(
          element =>
            (element?.included?.toString()?.includes(this.api.getUserId()) && element?.permission?.toString()?.includes('resolve_comments')) ||
            (element?.included?.toString()?.includes(this.api.getRoleId()) && element?.permission?.toString()?.includes('resolve_comments')) ||
            (element?.included?.some(ele => teamId?.toString()?.includes(ele)) && element?.permission?.toString()?.includes('resolve_comments'))
        );
        let resultPermissionDelete = response['data']?.listing_approval_detail?.some(
          element =>
            (element?.included?.toString()?.includes(this.api.getUserId()) && element?.permission?.toString()?.includes('delete_comments')) ||
            (element?.included?.toString()?.includes(this.api.getRoleId()) && element?.permission?.toString()?.includes('delete_comments')) ||
            (element?.included?.some(ele => teamId?.toString()?.includes(ele)) && element?.permission?.toString()?.includes('delete_comments'))
        );
        let resultPermissionWrite = response['data']?.listing_approval_detail?.some(
          element =>
            (element?.included?.toString()?.includes(this.api.getUserId()) && element?.permission?.toString()?.includes('edit_comment')) ||
            (element?.included?.toString()?.includes(this.api.getRoleId()) && element?.permission?.toString()?.includes('edit_comment')) ||
            (element?.included?.some(ele => teamId?.toString()?.includes(ele)) && element?.permission?.toString()?.includes('edit_comment'))
        );

        let resultAgent = response?.listing_agents?.toString()?.includes(this.api.getUserId());
        let isApproved =
          resultPermissionApproval || resultAgent || this.userRole === 'master admin' || this.userRole === 'operations manager' ? true : false;
        let isDeleted =
          resultPermissionDelete || resultAgent || this.userRole === 'master admin' || this.userRole === 'operations manager' ? true : false;
        let isResolved =
          resultPermissionResolve || resultAgent || this.userRole === 'master admin' || this.userRole === 'operations manager' ? true : false;
        let isWritable =
          resultPermissionWrite || resultAgent || this.userRole === 'master admin' || this.userRole === 'operations manager' ? true : false;

        this.readWritePermissions = {
          ...this.readWritePermissions,
          can_approve: isApproved,
          can_delete: isDeleted,
          can_resolve: isResolved,
          can_write: isWritable,
        };
      },
    });
  }

  /**
   * Sends approval request for a listing
   * If 'Go live now' is selected, publishes immediately, otherwise schedules it
   */
  sendApproval() {
    let approval_param = {
      listing_id: this.editPropertyId,
      is_approved: true,
      publish_date: this.livePlan === 'Go live now' ? new Date() : this.schedule,
      approval_request_by: null,
    };
    this._listingApprovalService.up_dateListingApprovalStatus(approval_param).subscribe({
      next: () => {
        this.toastr.success('Approved successfully');
        this.getPublishDate(this.editPropertyId);
        this.livePlan = null;
        this.schedule = null;
      },
    });
  }

  onUpdateMSelect(eve, type, index) {
    let ids = eve.map(item => item._id);
    if (type == 'email_bcc') {
      this.email_bcc[index].email_bcc = ids;
    } else if (type == 'email_cc') {
      this.email_cc[index].email_cc = ids;
    } else if (type == 'email_to') {
      this.email_to[index].email_to = ids;
    }
  }

  /**
   * Sends an email related to a listing
   * Ensures subject and message are provided before sending
   * Resets email-related fields after successful email dispatch
   */
  sendEmail() {
    if (this.email_subject && this.email_message) {
      this.emailBtnDisabled = true;
      let email_data = {
        subject: this.email_subject,
        email_notes: this.email_message,
        email_to: this.email_to,
        email_cc: this.email_cc,
        email_bcc: this.email_bcc,
        office_id: this.api.getOfficeId(),
        user_id: this.api.getUserId(),
      };
      this.listingPropertiesService.listingPreviewEmailToSeller(this.editPropertyId, email_data).subscribe({
        next: () => {
          this.toastr.success('Email sent successfully');
          this.show_email_template = false;
          this.email_to = [];
          this.email_cc = [];
          this.email_bcc = [];
          this.email_subject = null;
          this.email_message = null;
          this.emailTemplates = null;
          this.emailBtnDisabled = false;
        },
      });
    }
  }

  // Fetches the publish date for a given listing ID
  getPublishDate(id) {
    this.listingPropertiesService.getListingPropertyDetailById(id).subscribe(response => {
      this.publish_date = response?.data?.publish_date;
    });
  }

  // Handles changes in the image carousel navigation
  onCarouselChange(event) {
    this.imagesPath = null;
    if (this.currentIndex >= this.listingDetails?.property_images?.length - 3 && this.currentIndex < this.listingDetails?.property_images?.length) {
      this.currentIndex = this.currentIndex + 1;
    } else if (this.currentIndex >= this.listingDetails?.property_images?.length) {
      this.currentIndex = 1;
    } else {
      this.currentIndex = event.page;
    }
    this.imageIndex = this.currentIndex;
    let imgPath = this.listingDetails?.property_images[this.currentIndex];
    this.imagesPath = imgPath?.popupPath ? imgPath?.popupPath : imgPath?.thumbPath;
  }

  // Navigates to the next image in the carousel
  goToNext() {
    this.currentIndex = (this.currentIndex + 1) % this.listingDetails?.property_images?.length;
    this.updateImagePath();
  }

  // Navigates to the previous image in the carousel
  goToPrevious() {
    this.currentIndex = (this.currentIndex - 1 + this.listingDetails?.property_images?.length) % this.listingDetails?.property_images?.length;
    this.updateImagePath();
  }

  // Updates the displayed images and image path based on the current index
  updateImagePath() {
    const totalImages = this.listingDetails?.property_images.length || 0;
    const endIndex = this.currentIndex + 3;
    const imgPath = this.listingDetails?.property_images[this.currentIndex];
    this.imagesPath = imgPath?.popupPath || imgPath?.thumbPath;
    this.imageIndex = this.currentIndex + 1;
    if (endIndex <= totalImages) {
      this.displayedImages = this.listingDetails?.property_images.slice(this.currentIndex, endIndex);
    } else {
      // Wrap around the array to get remaining images from the start
      this.displayedImages = [
        ...this.listingDetails?.property_images.slice(this.currentIndex, totalImages),
        ...this.listingDetails?.property_images.slice(0, endIndex - totalImages),
      ];
    }
  }

  // Sets the image path based on the selected item
  getImagePath(item: any) {
    this.imagesPath = null;
    let ind = this.listingDetails?.property_images?.findIndex(itm => {
      return itm._id === item._id;
    });
    this.imageIndex = ind + 1;
    this.imagesPath = item?.popupPath ? item?.popupPath : item?.thumbPath;
  }

  // Retrieves the image URL based on its path
  getImageByPath(item) {
    return item?.popupPath ? this.S3Path + item?.popupPath : this.S3Path + item?.thumbPath;
  }

  // Retrieves the slider image URL based on its path
  getSliderImageByPath(item) {
    return item?.popupPath ? this.S3Path + item?.popupPath : this.S3Path + item?.thumbPath;
  }

  // Formats the given date-time into a readable time format
  getTimeFormat(dateTime) {
    return moment(toWallClock(dateTime)).format('hh:mm A');
  }

  // Closes the popup
  close() {
    this.hide();
  }

  // Displays the popup
  show() {
    this.popupLoad = true;
  }

  // Hides the popup and email template
  hide() {
    this.popupLoad = false;
    this.show_email_template = false;
  }

  // Fetches approval comments for a listing
  getApprovalComments(listingId: string = this.editPropertyId) {
    this._listingApprovalService.getCommentsOnListingApproval(listingId).subscribe({
      next: response => {
        const { status, data } = response;
        if (status && data !== null) {
          this.listingComments = data?.listing_comments?.map((bubble: any, index: number) => (bubble = { ...bubble, label: index + 1 })) || [];
          this.vendorComments = data?.vendor_comment;
        } else {
          this.listingComments = [];
        }
      },
      error: () => {
        this.listingComments = [];
      },
    });
  }

  // Updates listing comments based on new data
  onUpdateCallback(value: any) {
    let bubbleContainer = document.querySelectorAll(`.bubble-container`) as NodeListOf<Element>;
    bubbleContainer?.length > 0 && bubbleContainer.forEach(node => node?.remove());
    if (value?.listing_comments) {
      this.listingComments = value?.listing_comments || [];
    } else {
      this.getApprovalComments();
    }
  }
}
